import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Card, MenuItem, Stack, TextField, Typography, useTheme } from '@mui/material';
import { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import RHFTextArea from 'src/components/hook-form/rhf-text-area';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import { Controller } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import RHFRadioGroupProduct from './rhf-radio-group-produc';
import RHFEditor from 'src/components/hook-form/rhf-editor';
import { useMyDataStore } from 'src/store/me/me.store';
import CommentDialog from './commentary-dialog';
import RefuseDialog from './refuse-dialog';
import ApproveDialog from './approve-dialog';
import Iconify from 'src/components/iconify';

type Props = {
  hook: any;
};

export default function CouponForm({ hook }: Props) {
  const theme = useTheme();

  const user = useMyDataStore((state) => state.me);

  const hasRequiredRole = user?.roles?.some((role: string) =>
    ['lp_shopkeeper', 'lp_exhibitor', 'lp_contractor'].includes(role)
  );

  const isCnAdmin = user?.roles?.some(
    (role: string) => role === 'cn_admin' || role === 'lp_promoter'
  );

  const isLpExhibitor = user?.roles?.some((role: string) => role === 'lp_exhibitor');

  const getTextFromHtml = (html: string) => {
    if (!html) return '';
    // Remove tags HTML e entidades
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // const getFields = () => {
  //   if (hook.localType === 'company') {
  //     return (
  //       <RHFAutocomplete
  //         sx={{ mt: 2 }}
  //         options={
  //           hook.companies?.map((company: any) => ({
  //             value: company._id,
  //             label: company.name,
  //           })) || []
  //         }
  //         name="company"
  //         label="Empresa"
  //       />
  //     );
  //   }

  //   if (hook.localType === 'local') {
  //     return (
  //       <RHFAutocomplete
  //         sx={{ mt: 2 }}
  //         options={
  //           hook.renderedLocations.filter(
  //             (op: { value: string; label: string }) => op.value !== 'all'
  //           ) || []
  //         }
  //         name="local"
  //         label="Localização"
  //       />
  //     );
  //   }

  //   return null;
  // };

  function lpEventsForm() {
    return (
      <>
        <RHFSelect
          name="businessUnitId"
          label="Unidade de negócio"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
          disabled
        >
          {hook.businessUnits?.map((option: any, index: number) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>
        <RHFSelect
          name="lpEvent"
          label="Evento LP"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
          disabled
        >
          {hook.lpEventsByBusinessUnit?.map((option: any, index: number) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            <Typography variant="body1" fontWeight="bold">
              Imagem:
            </Typography>
          </Stack>

          <RHFUploadSingleFileAccelerator
            name="image"
            acceptAll={false}
            // @ts-ignore
            onDrop={hook.handleDrop}
          />

          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            * A imagem deve ter no máximo 800x800 pixels e 600kB
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <RHFTextField
            name="name"
            label="Nome"
            inputProps={{
              maxLength: 100,
            }}
            onChange={(e) => {
            const { value } = e.target;
            const formatted = value.charAt(0).toUpperCase() + value.slice(1);
            hook.setValue('name', formatted);
          }}
          />
          <Typography
            variant="caption"
            color={hook.watch('name')?.length > 75 ? 'warning.main' : 'text.secondary'}
            sx={{ textAlign: 'right' }}
          >
            {hook.watch('name')?.length || 0}/100 caracteres
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <RHFTextArea
            name="description"
            label="Descrição"
            inputProps={{
              maxLength: 300,
            }}
          />
          <Typography
            variant="caption"
            color={hook.watch('description')?.length > 250 ? 'warning.main' : 'text.secondary'}
            sx={{ textAlign: 'right' }}
          >
            {hook.watch('description')?.length || 0}/300 caracteres
          </Typography>
        </Stack>

        <RHFSelect
          name="category"
          label="Categoria"
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
        >
          {hook.categories?.map((option: any, index: number) => (
            <MenuItem key={index + 20} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <RHFSelect
          name="theme"
          label="Tema"
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: '3px',
          }}
        >
          {hook.themes?.map((option: any, index: number) => (
            <MenuItem key={index + 20} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </RHFSelect>

        <RHFEditor
          name="rules"
          label="Regras"
          onChange={(value) => {
            const textOnly = getTextFromHtml(value);
            if (textOnly.length <= 1000) {
              hook.setValue('rules', value);
            }
          }}
        />
        <Typography
          variant="caption"
          color={
            getTextFromHtml(hook.watch('rules') || '').length > 800
              ? 'warning.main'
              : 'text.secondary'
          }
          sx={{ textAlign: 'right' }}
        >
          {getTextFromHtml(hook.watch('rules') || '').length}/1000 caracteres
        </Typography>

        <Stack>
          <Stack
            direction="row"
            width="100%"
            sx={{
              alignItems: 'center',
            }}
            spacing={4}
          >
            <Controller
              name="startDate"
              control={hook.control}
              render={({ field }) => (
                <DatePicker
                  sx={{
                    flex: 1,
                  }}
                  closeOnSelect
                  label="Data de Início"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: '',
                      inputProps: {
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  {...field}
                />
              )}
            />
            <Controller
              name="endDate"
              control={hook.control}
              render={({ field }) => (
                <DatePicker
                  sx={{
                    flex: 1,
                  }}
                  closeOnSelect
                  label="Data de Fim"
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: '',
                      inputProps: {
                        readOnly: true,
                      },
                    },
                    day: {
                      sx: {
                        '&.MuiPickersDay-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: theme.palette.common.white,
                        },
                      },
                    },
                  }}
                  {...field}
                />
              )}
            />
          </Stack>
          {!hook.isValidEndDate && (
            <Typography
              sx={{
                pl: 1.8,
                pt: 1,
              }}
              variant="caption"
              color="error"
            >
              Data de término deve ser maior ou igual do que a data de início
            </Typography>
          )}
        </Stack>

        <Stack>
          {/* <Typography variant="body1" fontWeight="bold">
            Tipo de local:
          </Typography>
          <RHFRadioGroupLegacy
            name="localType"
            options={hook.LOCAL_TYPE_OPTIONS}
            getOptionLabel={hook.LOCAL_TYPE_LABEL}
          /> */}
          {/* {getFields()} */}
          <RHFAutocomplete
            sx={{ mt: 2 }}
            options={
              hook.renderedLocations.filter(
                (op: { value: string; label: string }) => op.value !== 'all'
              ) || []
            }
            disabled={isLpExhibitor}
            name="local"
            label="Localização"
          />
        </Stack>

        {hook.reward === 'code' && (
          <Stack>
            <Typography>Tipo de exibição dos códigos/links:</Typography>
            <RHFRadioGroupProduct
              name="codeShowType"
              options={hook.PRODUCT_CODE_OPTIONS}
              getOptionLabel={hook.PRODUCT_CODE_LABEL}
            />
          </Stack>
        )}

        {hook.reward === 'code' && (
          <Stack
            direction="row"
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
            spacing={2}
          >
            <Button variant="soft" onClick={() => hook.setOpen(true)}>
              Cadastrar Códigos/Links
            </Button>
            {hook.hasCodesToDownload &&(
              <Button variant="soft" onClick={hook.handleDownloadCodes} startIcon={<Iconify icon="solar:download-linear" width={18} />}>
                Baixar planilha
              </Button>
            )}
            <Typography variant="overline">
              {hook?.codesEdited
                ? `${hook?.codes?.length || 0} Códigos/Links Cadastrados`
                : `${hook.codes.length * hook.redeemCodeQuantity || 0} Códigos/Links Cadastrados`}
            </Typography>
          </Stack>
        )}

        <RHFTextField
          type="number"
          name="expirationDays"
          label="Tempo de retirada após resgate (em dias)"
        />

        <RHFTextField
          name="userPurchaseLimit"
          label="Quantidade de resgates por usuário"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          onKeyPress={(e) => {
            if (!/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
        />

        {/* <RHFTextField name="priority" label="Nível de prioridade" type="number" /> */}

        {/* <Stack spacing={1}>
          <Typography variant="body1" fontWeight="bold">
            Benefício de estacionamento:
          </Typography>
          <RHFRadioGroupProduct
            name="parking"
            options={hook.BOOLEAN_OPTIONS}
            getOptionLabel={hook.BOOLEAN_LABELS}
          />
        </Stack> */}

        {/* {hook.parking && (
          <RHFSelect name="parkingId" label="Identificador da Garagem" variant="outlined">
            {hook.parkings.map((option: any) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </RHFSelect>
        )} */}

        {/* <Stack>
          <Typography variant="body1" fontWeight="bold">
            Status:
          </Typography>
          <RHFRadioGroupLegacy
            name="status"
            options={hook.STATUS_OPTIONS}
            getOptionLabel={hook.STATUS_LABEL}
          />
        </Stack> */}
      </>
    );
  }

  return (
    <>
      <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
        {hook?.couponDetails?.coupon?.lpEventsData?.editComment &&
          hook?.couponDetails?.coupon?.lpEventsData?.approvalStatus === 'pending' &&
          hook?.isEdit &&
          isCnAdmin && (
            <>
              <Card sx={{ p: 4 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1">Motivo da Edição</Typography>
                  <TextField
                    disabled
                    value={hook?.couponDetails?.coupon?.lpEventsData?.editComment}
                    multiline
                    minRows={4}
                    maxRows={6}
                  />
                </Stack>
              </Card>
              <Box height={32} />
            </>
          )}
        {hook?.couponDetails?.coupon?.lpEventsData?.declineComment &&
          hook?.couponDetails?.coupon?.lpEventsData?.approvalStatus === 'declined' &&
          hook?.isEdit && (
            <>
              <Card sx={{ p: 4 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1">Motivo da Recusa</Typography>
                  <TextField
                    disabled
                    value={hook?.couponDetails?.coupon?.lpEventsData?.declineComment}
                    multiline
                    minRows={4}
                    maxRows={6}
                  />
                </Stack>
              </Card>
              <Box height={32} />
            </>
          )}
        <Card>
          <Stack spacing={2} p={4}>
            <Stack spacing={2}>
              {/* <RHFSelect
                name="exhibitionEnv"
                label="Ambiente de exibição"
                variant="outlined"
                sx={{
                  width: '100%',
                  borderRadius: '3px',
                }}
                disabled
              >
                {hook.EXHIBITION_ENV_OPTIONS?.map((option: any, index: number) => (
                  <MenuItem key={index + 20} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect> */}

              {lpEventsForm()}

              {hasRequiredRole && (
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  {hook.isEdit &&
                    hook?.couponDetails?.coupon?.lpEventsData?.approvalStatus === 'approved' && (
                      <LoadingButton
                        onClick={() => hook.handleActivateAndInactivate()}
                        variant="contained"
                        disabled={
                          user?.roles.includes('lp_contractor') &&
                          hook?.couponDetails?.coupon?.lpEventsData?.creatorId !== user._id &&
                          hook?.isEdit
                        }
                      >
                        {hook?.couponDetails?.coupon?.status === 'active'
                          ? 'Inativar Cupom'
                          : 'Ativar Cupom'}
                      </LoadingButton>
                    )}
                  <LoadingButton
                    {...(!hook.isEdit
                      ? { type: 'submit' }
                      : { onClick: () => hook.setOpenComment(true) })}
                    variant="contained"
                    loading={hook.isSubmitting}
                    disabled={
                      user?.roles.includes('lp_contractor') &&
                      hook?.couponDetails?.coupon?.lpEventsData?.creatorId !== user?._id &&
                      hook?.isEdit
                    }
                  >
                    {!hook.isEdit ? 'Cadastrar Cupom' : 'Editar Cupom'}
                  </LoadingButton>
                </Stack>
              )}
              {isCnAdmin && hook.isEdit && (
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                  <LoadingButton
                    disabled={
                      hook?.couponDetails?.coupon?.lpEventsData?.approvalStatus === 'approved'
                    }
                    onClick={() => hook.handleApprove()}
                    variant="contained"
                  >
                    Aprovar Cupom
                  </LoadingButton>
                  <LoadingButton
                    disabled={
                      hook?.couponDetails?.coupon?.lpEventsData?.approvalStatus === 'declined'
                    }
                    onClick={() => hook.handleRefuse()}
                    variant="contained"
                  >
                    Recusar Cupom
                  </LoadingButton>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Card>
        <CommentDialog hook={hook} />
      </FormProvider>
      <RefuseDialog hook={hook} />
      <ApproveDialog hook={hook} />
    </>
  );
}
